// Deployment host derivation (DEC-001, DEC-038, DEC-040). Plain ESM + `deployment.d.ts` so the same
// helper runs in the browser bundles, in Vite configs and in the Node build scripts (Node 20, no TS
// loader). DEC-040: every frontend ships from ONE origin `app.<root>` (console `/`, admin `/admin/`,
// web app `/chat/<slug>/`, widget `/widget.js` + `/v/*`); the API stays on `api.<root>`.

export const DEPLOY_ENVS = Object.freeze(["production", "staging"]);
export const DEFAULT_DEPLOY_ENV = "production";
export const DEFAULT_ROOT_DOMAIN = "sagot.cruditsolutions.com";

/** Staging (deferred, DEC-039) hosts are `staging-<label>.<root>`. */
export const STAGING_HOST_PREFIX = "staging-";
export const HOST_LABELS = Object.freeze({ app: "app", api: "api" });

/** Path prefixes of the single-origin deployment (DEC-040). The console never routes these. */
export const BASE_PATHS = Object.freeze({ console: "/", admin: "/admin/", webapp: "/chat/" });
export const WIDGET_LOADER_PATH = "/widget.js";
export const WIDGET_ASSET_PREFIX = "/v/";
export const RESERVED_CONSOLE_PREFIXES = Object.freeze(["/admin", "/chat", "/widget.js", "/v", "/assets"]);

/** Empty strings count as unset (Terraform skips empty values; `.env` files may leave them blank). */
function clean(value) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed === "" ? undefined : trimmed;
}

/**
 * `VITE_ENV` wins and must be `production` or `staging`. Unset → inferred from `VITE_ROOT_DOMAIN`
 * (a `staging.` root means staging), else production.
 */
export function resolveDeployEnv(env, rootDomain) {
  const explicit = clean(env);
  if (explicit !== undefined) {
    if (!DEPLOY_ENVS.includes(explicit)) throw new Error(`VITE_ENV must be one of ${DEPLOY_ENVS.join(", ")} (got "${explicit}")`);
    return explicit;
  }
  return clean(rootDomain)?.startsWith("staging.") ? "staging" : DEFAULT_DEPLOY_ENV;
}

/** Resolve the platform + API hosts for one environment. */
export function resolveDeployment({ env, rootDomain } = {}) {
  const deployEnv = resolveDeployEnv(env, rootDomain);
  const staging = deployEnv === "staging";
  const given = (clean(rootDomain) ?? DEFAULT_ROOT_DOMAIN).toLowerCase();
  const platformRoot = staging && given.startsWith("staging.") ? given.slice("staging.".length) : given;
  const prefix = staging ? STAGING_HOST_PREFIX : "";
  const host = (label) => `${prefix}${label}.${platformRoot}`;
  return Object.freeze({
    env: deployEnv,
    platformRoot,
    hosts: Object.freeze({ app: host(HOST_LABELS.app), api: host(HOST_LABELS.api) }),
  });
}

/**
 * API origin from the build env: `VITE_API_URL` (Terraform), else the deprecated
 * `VITE_API_BASE_URL` (read for one release only), else undefined. Trailing slashes stripped.
 */
export function pickApiUrl(env) {
  return (clean(env?.VITE_API_URL) ?? clean(env?.VITE_API_BASE_URL))?.replace(/\/+$/, "");
}

/** `https://<host>` unless an explicit URL override is set. */
export function originOf(url, fallbackHost) {
  const value = clean(url);
  return value === undefined ? `https://${fallbackHost}` : new URL(value).origin;
}
